import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  Container,
  FormControl,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarMonth as CalendarMonthIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';

import { useAuth } from '../../contexts/AuthContext';
import { agendaService } from '../../services/agendaService';
import PageBackButton from '../../components/PageBackButton';
import {
  AgendaDayException,
  AgendaEntry,
  AgendaHolidayImportResult,
  AgendaStatus,
  CreateAgendaEntryDTO,
  UpdateAgendaAvailabilityDTO,
  UpdateAgendaEntryDTO,
} from '../../types/agenda';

const defaultFormState = {
  title: '',
  notes: '',
  startsAt: '',
  endsAt: '',
  status: 'planned' as AgendaStatus,
  isVisibleInProfile: false,
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};

const toLocalDateTimeInputValue = (date: Date): string => {
  const pad = (value: number) => value.toString().padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const parseDateTimeInput = (value: string): number | null => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

const dateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const weekdayFieldByIndex: Array<keyof UpdateAgendaAvailabilityDTO> = [
  'works_monday',
  'works_tuesday',
  'works_wednesday',
  'works_thursday',
  'works_friday',
  'works_saturday',
  'works_sunday',
];

const holidayCountryOptions = ['BG', 'NL'];

const statusColorMap: Record<AgendaStatus, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  planned: 'info',
  busy: 'warning',
  completed: 'success',
  cancelled: 'error',
};

const ProfessionalAgenda: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const professionalId = Number(id);
  const isOwnAgenda = Boolean(user?.id && professionalId && user.id === professionalId);
  const [formState, setFormState] = useState(defaultFormState);
  const [editingEntryId, setEditingEntryId] = useState<number | null>(null);
  const [exceptionDate, setExceptionDate] = useState('');
  const [exceptionLabel, setExceptionLabel] = useState('');
  const [exceptionWorkingOverride, setExceptionWorkingOverride] = useState(false);
  const [holidayImportYear, setHolidayImportYear] = useState(`${new Date().getFullYear()}`);
  const [holidayImportResult, setHolidayImportResult] = useState<AgendaHolidayImportResult | null>(null);
  const [viewMode, setViewMode] = useState<'month' | 'list'>('month');
  const dayClickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [monthCursor, setMonthCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const { data: entries = [], isLoading, error } = useQuery({
    queryKey: ['professionalAgenda', professionalId],
    queryFn: () => agendaService.getMyEntries(),
    enabled: isOwnAgenda,
  });

  const { data: availability } = useQuery({
    queryKey: ['professionalAgendaAvailability', professionalId],
    queryFn: () => agendaService.getAvailability(),
    enabled: isOwnAgenda,
  });

  const { data: exceptions = [] } = useQuery({
    queryKey: ['professionalAgendaExceptions', professionalId],
    queryFn: () => agendaService.getExceptions(),
    enabled: isOwnAgenda,
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateAgendaEntryDTO) => agendaService.createEntry(payload),
    onSuccess: () => {
      setFormState(defaultFormState);
      queryClient.invalidateQueries({ queryKey: ['professionalAgenda', professionalId] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ entryId, payload }: { entryId: number; payload: UpdateAgendaEntryDTO }) =>
      agendaService.updateEntry(entryId, payload),
    onSuccess: () => {
      setFormState(defaultFormState);
      setEditingEntryId(null);
      queryClient.invalidateQueries({ queryKey: ['professionalAgenda', professionalId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (entryId: number) => agendaService.deleteEntry(entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professionalAgenda', professionalId] });
    },
  });

  const updateAvailabilityMutation = useMutation({
    mutationFn: (payload: UpdateAgendaAvailabilityDTO) => agendaService.updateAvailability(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professionalAgendaAvailability', professionalId] });
    },
  });

  const createExceptionMutation = useMutation({
    mutationFn: () =>
      agendaService.createException({
        date: exceptionDate,
        is_working_day: exceptionWorkingOverride,
        label: exceptionLabel.trim() || undefined,
      }),
    onSuccess: () => {
      setExceptionDate('');
      setExceptionLabel('');
      setExceptionWorkingOverride(false);
      queryClient.invalidateQueries({ queryKey: ['professionalAgendaExceptions', professionalId] });
    },
  });

  const deleteExceptionMutation = useMutation({
    mutationFn: (exceptionId: number) => agendaService.deleteException(exceptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professionalAgendaExceptions', professionalId] });
    },
  });

  const importHolidaysMutation = useMutation({
    mutationFn: ({ country, year }: { country: string; year: number }) => agendaService.importHolidays(country, year),
    onSuccess: (result) => {
      setHolidayImportResult(result);
      queryClient.invalidateQueries({ queryKey: ['professionalAgendaExceptions', professionalId] });
    },
  });

  const sortedEntries = useMemo(
    () => [...entries].sort((a: AgendaEntry, b: AgendaEntry) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()),
    [entries]
  );

  const conflictEntries = useMemo(() => {
    const candidateStart = parseDateTimeInput(formState.startsAt);
    if (candidateStart === null) return [];
    const parsedEnd = parseDateTimeInput(formState.endsAt);
    const candidateEnd = parsedEnd ?? candidateStart;

    return sortedEntries.filter((entry) => {
      if (editingEntryId !== null && entry.id === editingEntryId) return false;

      const entryStart = new Date(entry.starts_at).getTime();
      if (Number.isNaN(entryStart)) return false;
      const rawEntryEnd = entry.ends_at ? new Date(entry.ends_at).getTime() : NaN;
      const entryEnd = Number.isNaN(rawEntryEnd) ? entryStart : rawEntryEnd;

      return candidateStart <= entryEnd && entryStart <= candidateEnd;
    });
  }, [editingEntryId, formState.endsAt, formState.startsAt, sortedEntries]);

  const exceptionByDate = useMemo(() => {
    const map = new Map<string, AgendaDayException>();
    exceptions.forEach((item) => map.set(item.date, item));
    return map;
  }, [exceptions]);

  const isNonWorkingDay = (day: Date): boolean => {
    const key = dateKey(day);
    const exception = exceptionByDate.get(key);
    if (exception) return !exception.is_working_day;
    if (!availability) return day.getDay() === 0 || day.getDay() === 6;
    const weekdayIndex = (day.getDay() + 6) % 7;
    const field = weekdayFieldByIndex[weekdayIndex];
    return !availability[field];
  };

  const entriesByDay = useMemo(() => {
    const map = new Map<string, AgendaEntry[]>();
    sortedEntries.forEach((entry) => {
      const key = dateKey(new Date(entry.starts_at));
      const existing = map.get(key) || [];
      existing.push(entry);
      map.set(key, existing);
    });
    return map;
  }, [sortedEntries]);

  const monthDays = useMemo(() => {
    const year = monthCursor.getFullYear();
    const month = monthCursor.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const firstWeekday = (firstDay.getDay() + 6) % 7;
    const totalDays = lastDay.getDate();
    const days: Date[] = [];

    for (let i = 0; i < firstWeekday; i += 1) {
      days.push(new Date(year, month, i - firstWeekday + 1));
    }
    for (let day = 1; day <= totalDays; day += 1) {
      days.push(new Date(year, month, day));
    }
    while (days.length % 7 !== 0) {
      days.push(new Date(year, month + 1, days.length - (firstWeekday + totalDays) + 1));
    }
    return days;
  }, [monthCursor]);

  const weekdayHeaders = useMemo(() => {
    const baseDate = new Date(2026, 0, 5);
    return Array.from({ length: 7 }, (_, index) =>
      new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate() + index).toLocaleDateString(i18n.language, {
        weekday: 'short',
      })
    );
  }, [i18n.language]);

  const monthLabel = useMemo(
    () => monthCursor.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' }),
    [i18n.language, monthCursor]
  );

  const submitDisabled =
    createMutation.isPending ||
    updateMutation.isPending ||
    !formState.title.trim() ||
    !formState.startsAt ||
    (Boolean(formState.endsAt) && new Date(formState.endsAt).getTime() < new Date(formState.startsAt).getTime());

  const handleSubmitEntry = (event: React.FormEvent) => {
    event.preventDefault();
    if (submitDisabled) return;

    const payload: CreateAgendaEntryDTO = {
      title: formState.title.trim(),
      notes: formState.notes.trim() || undefined,
      starts_at: new Date(formState.startsAt).toISOString(),
      ends_at: formState.endsAt ? new Date(formState.endsAt).toISOString() : undefined,
      status: formState.status,
      is_visible_in_profile: formState.isVisibleInProfile,
    };

    if (editingEntryId !== null) {
      updateMutation.mutate({ entryId: editingEntryId, payload });
      return;
    }

    createMutation.mutate(payload);
  };

  const handlePickCalendarDay = (day: Date) => {
    const preset = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 9, 0, 0, 0);
    setFormState((prev) => ({
      ...prev,
      startsAt: toLocalDateTimeInputValue(preset),
      endsAt: prev.endsAt || toLocalDateTimeInputValue(new Date(preset.getTime() + 60 * 60 * 1000)),
    }));
  };

  const clearDayClickTimeout = () => {
    if (dayClickTimeoutRef.current) {
      clearTimeout(dayClickTimeoutRef.current);
      dayClickTimeoutRef.current = null;
    }
  };

  const handleCalendarDayClick = (day: Date) => {
    clearDayClickTimeout();
    dayClickTimeoutRef.current = setTimeout(() => {
      handlePickCalendarDay(day);
      dayClickTimeoutRef.current = null;
    }, 220);
  };

  const handleCalendarDayDoubleClick = (day: Date, dayEntries: AgendaEntry[]) => {
    clearDayClickTimeout();
    if (dayEntries.length > 0) {
      handleStartEdit(dayEntries[0]);
      return;
    }
    handlePickCalendarDay(day);
  };

  const handleStartEdit = (entry: AgendaEntry) => {
    setEditingEntryId(entry.id);
    setFormState({
      title: entry.title,
      notes: entry.notes || '',
      startsAt: toLocalDateTimeInputValue(new Date(entry.starts_at)),
      endsAt: entry.ends_at ? toLocalDateTimeInputValue(new Date(entry.ends_at)) : '',
      status: entry.status,
      isVisibleInProfile: entry.is_visible_in_profile,
    });
  };

  const handleCancelEdit = () => {
    setEditingEntryId(null);
    setFormState(defaultFormState);
  };

  const handleToggleWeekday = (field: keyof UpdateAgendaAvailabilityDTO, checked: boolean) => {
    if (!availability) return;
    updateAvailabilityMutation.mutate({
      works_monday: availability.works_monday,
      works_tuesday: availability.works_tuesday,
      works_wednesday: availability.works_wednesday,
      works_thursday: availability.works_thursday,
      works_friday: availability.works_friday,
      works_saturday: availability.works_saturday,
      works_sunday: availability.works_sunday,
      holiday_country: availability.holiday_country,
      [field]: checked,
    });
  };

  const handleHolidayCountryChange = (country: string) => {
    if (!availability) return;
    updateAvailabilityMutation.mutate({
      works_monday: availability.works_monday,
      works_tuesday: availability.works_tuesday,
      works_wednesday: availability.works_wednesday,
      works_thursday: availability.works_thursday,
      works_friday: availability.works_friday,
      works_saturday: availability.works_saturday,
      works_sunday: availability.works_sunday,
      holiday_country: country,
    });
  };

  const handleCreateException = (event: React.FormEvent) => {
    event.preventDefault();
    if (!exceptionDate || createExceptionMutation.isPending) return;
    createExceptionMutation.mutate();
  };

  const handleImportHolidays = () => {
    const year = Number(holidayImportYear);
    if (Number.isNaN(year) || year < 2020 || year > 2100) return;
    importHolidaysMutation.mutate({
      country: availability?.holiday_country || 'BG',
      year,
    });
  };

  useEffect(() => () => clearDayClickTimeout(), []);

  if (!isOwnAgenda) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <PageBackButton />
        <Alert severity="warning">{t('agenda.access_denied')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageBackButton />
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h4" component="h1">
            {t('agenda.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('agenda.subtitle')}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate(`/profile/professional/${professionalId}`)}>
          {t('agenda.back_to_profile')}
        </Button>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Stack spacing={2}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('agenda.availability.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t('agenda.availability.subtitle')}
              </Typography>
              <FormGroup>
                {weekdayFieldByIndex.map((field, index) => (
                  <FormControlLabel
                    key={field}
                    control={
                      <Checkbox
                        checked={Boolean(availability?.[field])}
                        onChange={(event) => handleToggleWeekday(field, event.target.checked)}
                        disabled={!availability || updateAvailabilityMutation.isPending}
                      />
                    }
                    label={String(
                      t(
                        `agenda.weekdays.${['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][index]}`
                      )
                    )}
                  />
                ))}
              </FormGroup>
              <FormControl fullWidth sx={{ mt: 1.5 }}>
                <InputLabel id="holiday-country-label">{t('agenda.availability.holiday_country')}</InputLabel>
                <Select
                  labelId="holiday-country-label"
                  label={t('agenda.availability.holiday_country')}
                  value={availability?.holiday_country || 'BG'}
                  onChange={(event) => handleHolidayCountryChange(String(event.target.value))}
                  disabled={!availability || updateAvailabilityMutation.isPending}
                >
                  {holidayCountryOptions.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('agenda.exceptions.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t('agenda.exceptions.subtitle')}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <TextField
                  label={t('agenda.exceptions.holiday_year')}
                  type="number"
                  value={holidayImportYear}
                  onChange={(event) => setHolidayImportYear(event.target.value)}
                  inputProps={{ min: 2020, max: 2100 }}
                />
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleImportHolidays}
                  disabled={importHolidaysMutation.isPending || !availability}
                >
                  {t('agenda.exceptions.import_holidays')}
                </Button>
              </Stack>
              {holidayImportResult && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {t('agenda.exceptions.import_result', {
                    country: holidayImportResult.country,
                    year: holidayImportResult.year,
                    created: holidayImportResult.created,
                    skipped: holidayImportResult.skipped,
                  })}
                </Alert>
              )}
              <Box component="form" onSubmit={handleCreateException} sx={{ display: 'grid', gap: 1.5, mb: 1.5 }}>
                <TextField
                  label={t('agenda.exceptions.date')}
                  type="date"
                  value={exceptionDate}
                  onChange={(event) => setExceptionDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <TextField
                  label={t('agenda.exceptions.label')}
                  value={exceptionLabel}
                  onChange={(event) => setExceptionLabel(event.target.value)}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={exceptionWorkingOverride}
                      onChange={(event) => setExceptionWorkingOverride(event.target.checked)}
                    />
                  }
                  label={t('agenda.exceptions.working_override')}
                />
                <Button type="submit" variant="outlined" disabled={!exceptionDate || createExceptionMutation.isPending}>
                  {t('agenda.exceptions.add')}
                </Button>
              </Box>
              <Stack spacing={1}>
                {exceptions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('agenda.exceptions.empty')}
                  </Typography>
                ) : (
                  exceptions.map((item) => (
                    <Stack key={item.id} direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Box>
                        <Typography variant="body2">{new Date(item.date).toLocaleDateString(i18n.language)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.is_working_day ? t('agenda.exceptions.working_day') : t('agenda.exceptions.non_working_day')}
                          {item.label ? ` • ${item.label}` : ''}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => deleteExceptionMutation.mutate(item.id)}
                        disabled={deleteExceptionMutation.isPending}
                      >
                        {t('common.delete')}
                      </Button>
                    </Stack>
                  ))
                )}
              </Stack>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {editingEntryId ? t('agenda.edit_entry') : t('agenda.add_entry')}
              </Typography>
              <Box component="form" onSubmit={handleSubmitEntry} sx={{ display: 'grid', gap: 2 }}>
                <TextField
                  label={t('agenda.fields.title')}
                  value={formState.title}
                  onChange={(event) => setFormState((prev) => ({ ...prev, title: event.target.value }))}
                  required
                />
                <TextField
                  label={t('agenda.fields.notes')}
                  value={formState.notes}
                  onChange={(event) => setFormState((prev) => ({ ...prev, notes: event.target.value }))}
                  multiline
                  minRows={3}
                />
                <TextField
                  label={t('agenda.fields.starts_at')}
                  type="datetime-local"
                  value={formState.startsAt}
                  onChange={(event) => setFormState((prev) => ({ ...prev, startsAt: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <TextField
                  label={t('agenda.fields.ends_at')}
                  type="datetime-local"
                  value={formState.endsAt}
                  onChange={(event) => setFormState((prev) => ({ ...prev, endsAt: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
                <FormControl fullWidth>
                  <InputLabel id="agenda-status-label">{t('agenda.fields.status')}</InputLabel>
                  <Select
                    labelId="agenda-status-label"
                    label={t('agenda.fields.status')}
                    value={formState.status}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, status: event.target.value as AgendaStatus }))
                    }
                  >
                    <MenuItem value="planned">{t('agenda.status.planned')}</MenuItem>
                    <MenuItem value="busy">{t('agenda.status.busy')}</MenuItem>
                    <MenuItem value="completed">{t('agenda.status.completed')}</MenuItem>
                    <MenuItem value="cancelled">{t('agenda.status.cancelled')}</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formState.isVisibleInProfile}
                      onChange={(event) =>
                        setFormState((prev) => ({ ...prev, isVisibleInProfile: event.target.checked }))
                      }
                    />
                  }
                  label={t('agenda.fields.visible_in_profile')}
                />
                {conflictEntries.length > 0 && (
                  <Alert severity="warning">
                    {t('agenda.conflict_warning', { count: conflictEntries.length })}
                  </Alert>
                )}
                <Stack direction="row" spacing={1}>
                  <Button type="submit" variant="contained" disabled={submitDisabled}>
                    {editingEntryId ? t('agenda.actions.update') : t('agenda.actions.create')}
                  </Button>
                  {editingEntryId && (
                    <Button type="button" variant="outlined" onClick={handleCancelEdit}>
                      {t('common.cancel')}
                    </Button>
                  )}
                </Stack>
              </Box>
            </Paper>
          </Stack>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6">
                {viewMode === 'month' ? t('agenda.month_title') : t('agenda.list_title')}
              </Typography>
              <ButtonGroup variant="outlined" size="small">
                <Button
                  onClick={() => setViewMode('month')}
                  variant={viewMode === 'month' ? 'contained' : 'outlined'}
                  startIcon={<CalendarMonthIcon />}
                >
                  {t('agenda.view.month')}
                </Button>
                <Button
                  onClick={() => setViewMode('list')}
                  variant={viewMode === 'list' ? 'contained' : 'outlined'}
                  startIcon={<ViewListIcon />}
                >
                  {t('agenda.view.list')}
                </Button>
              </ButtonGroup>
            </Stack>
            {isLoading ? (
              <Typography>{t('common.loading')}</Typography>
            ) : error ? (
              <Alert severity="error">{t('agenda.load_error')}</Alert>
            ) : sortedEntries.length === 0 ? (
              <Typography color="text.secondary">{t('agenda.empty')}</Typography>
            ) : viewMode === 'month' ? (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Button
                    size="small"
                    startIcon={<ChevronLeftIcon />}
                    onClick={() =>
                      setMonthCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
                    }
                  >
                    {t('common.back')}
                  </Button>
                  <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                    {monthLabel}
                  </Typography>
                  <Button
                    size="small"
                    endIcon={<ChevronRightIcon />}
                    onClick={() =>
                      setMonthCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
                    }
                  >
                    {t('common.next')}
                  </Button>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t('agenda.click_day_hint')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t('agenda.double_click_day_hint')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t('agenda.click_entry_hint')}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                    gap: 0.75,
                  }}
                >
                  {weekdayHeaders.map((header) => (
                    <Typography key={header} variant="caption" sx={{ fontWeight: 600 }}>
                      {header}
                    </Typography>
                  ))}
                  {monthDays.map((day) => {
                    const key = dateKey(day);
                    const dayEntries = entriesByDay.get(key) || [];
                    const nonWorkingDay = isNonWorkingDay(day);
                    const isCurrentMonth = day.getMonth() === monthCursor.getMonth();
                    return (
                      <Paper
                        key={key}
                        variant="outlined"
                        onClick={() => handleCalendarDayClick(day)}
                        onDoubleClick={() => handleCalendarDayDoubleClick(day, dayEntries)}
                        sx={{
                          minHeight: 110,
                          p: 0.75,
                          cursor: 'pointer',
                          opacity: isCurrentMonth ? 1 : 0.45,
                          backgroundColor: nonWorkingDay ? 'action.hover' : 'background.paper',
                          '&:hover': { borderColor: 'primary.main' },
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {day.getDate()}
                        </Typography>
                        <Stack spacing={0.4} sx={{ mt: 0.5 }}>
                          {nonWorkingDay && (
                            <Chip size="small" variant="outlined" label={t('agenda.non_working_day')} />
                          )}
                          {dayEntries.slice(0, 3).map((entry) => (
                            <Chip
                              key={entry.id}
                              size="small"
                              color={statusColorMap[entry.status]}
                              label={entry.title}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleStartEdit(entry);
                              }}
                              sx={{ justifyContent: 'flex-start' }}
                            />
                          ))}
                          {dayEntries.length > 3 && (
                            <Typography variant="caption" color="text.secondary">
                              +{dayEntries.length - 3} {t('agenda.more')}
                            </Typography>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Box>
              </>
            ) : (
              <Stack spacing={1.5}>
                {sortedEntries.map((entry) => (
                  <Paper key={entry.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle1">{entry.title}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                        <Chip
                          size="small"
                          label={t(`agenda.status.${entry.status}`)}
                          color={statusColorMap[entry.status]}
                        />
                        {entry.is_visible_in_profile && (
                          <Chip size="small" variant="outlined" label={t('agenda.visible_chip')} />
                        )}
                        <Button size="small" onClick={() => handleStartEdit(entry)}>
                          {t('common.edit')}
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          disabled={deleteMutation.isPending}
                          onClick={() => deleteMutation.mutate(entry.id)}
                        >
                          {t('common.delete')}
                        </Button>
                      </Stack>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {t('agenda.from')}: {formatDateTime(entry.starts_at)}
                    </Typography>
                    {entry.ends_at && (
                      <Typography variant="body2" color="text.secondary">
                        {t('agenda.to')}: {formatDateTime(entry.ends_at)}
                      </Typography>
                    )}
                    {entry.notes && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {entry.notes}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProfessionalAgenda;
